import SignOut from "./components/SignOut";
import UserDetailsNotAllowed from "./components/UserDetailsNotAllowed/UserDetailsNotAllowed";
import { useUser } from "contexts/UserContext";
import ChangeUserPassword from "pages/ChangeUserPassword/ChangeUserPassword";
import ErrorPage from "pages/Error/ErrorPage";
import { routes } from "pages/routes";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import RestrictedRoute from "./components/RestrictedRoute";
import * as Pages from "./asyncPages";
import { logError } from "services/logging";

export default function EndUserRoutes() {
  const location = useLocation();
  const user = useUser();

  if (user.isPurchaseRequired) {
    const fallbackRedirect = user.canPurchase
      ? routes.payment.path()
      : routes.settings.path();
    return (
      <Routes key={location.pathname}>
        <Route
          path={routes.payment.pattern}
          element={
            <RestrictedRoute permissions="student" Component={Pages.AsyncPaymentPage} />
          }
        />
        <Route
          path={routes.settings.pattern}
          element={
            <RestrictedRoute
              Component={Pages.AsyncStudentSettings}
              permissions="student"
            />
          }
        />
        <Route path="*" element={<Navigate to={fallbackRedirect} replace />} />
      </Routes>
    );
  }

  const getRootRedirect = () => {
    if (user.isTeacher) {
      return routes.teacherDashboard.path();
    }
    if (user.isGroupStudent) {
      return routes.libraryRoot.path();
    }
    if (user.isTeamManager || user.isCompanyManager) {
      return routes.teamDashboard.path();
    }
    if (user.isStudent) {
      return routes.studentDashboard.path();
    }

    logError("Unexpected authenticated user in EndUserRoutes", {
      roles: user.roles,
      userId: user.id,
    });
    return routes.error.path();
  };

  return (
    <Routes key={location.pathname}>
      <Route
        path={routes.root.pattern}
        element={<Navigate to={getRootRedirect()} replace />}
      />

      <Route
        path={routes.studentDashboard.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncStudentDashboard}
            permissions="student"
          />
        }
      />

      <Route
        path={routes.teacherDashboard.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncTeacherDashboard}
            permissions="instructor"
          />
        }
      />

      <Route
        path={routes.myProgress.pattern}
        element={
          <RestrictedRoute Component={Pages.AsyncMyProgress} permissions="student" />
        }
      />
      <Route
        path={routes.exercises.pattern}
        element={
          <RestrictedRoute Component={Pages.AsyncExercises} permissions="instructor" />
        }
      />
      <Route
        path={routes.exercisesByCreator.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncMyTeacherExerciseList}
            permissions="instructor"
          />
        }
      />
      <Route
        path={routes.exerciseCreate.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncExerciseCreator}
            permissions="instructor"
          />
        }
      />
      <Route
        path={routes.exerciseEdit.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncExerciseEditor}
            permissions="instructor"
          />
        }
      />
      <Route
        path={routes.exerciseDetails.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncExerciseDetails}
            permissions={["student", "instructor"]}
          />
        }
      />
      <Route
        path={routes.exerciseAssignmentDetails.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncExerciseAssignmentDetails}
            permissions={["student", "instructor"]}
          />
        }
      />
      <Route
        path={routes.speakingClub.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncSpeakingclubPage}
            permissions={usr => usr.canAccessSpeakingClub}
          />
        }
      />
      <Route
        path={routes.sessionFeedback.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncLessonFeedback}
            permissions={["student", "instructor"]}
            mode="view"
          />
        }
      />

      <Route
        path={routes.tagSearch.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncTagSearch}
            permissions={["student", "instructor"]}
          />
        }
      />
      <Route
        path={routes.sessionFeedbackEdit.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncLessonFeedback}
            permissions="instructor"
            mode="edit"
          />
        }
      />
      <Route
        path={routes.search.pattern}
        element={
          <RestrictedRoute Component={Pages.AsyncSearch} permissions="instructor" />
        }
      />

      <Route
        path={routes.settings.pattern}
        element={
          <RestrictedRoute
            Component={
              user.isTeacher ? Pages.AsyncTeacherSettings : Pages.AsyncStudentSettings
            }
            permissions={["student", "instructor"]}
          />
        }
      />
      <Route
        path={routes.students.pattern}
        element={
          <RestrictedRoute Component={Pages.AsyncStudents} permissions="instructor" />
        }
      />
      <Route
        path={routes.studentDetails.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncStudentDetails}
            permissions="instructor"
            notAllowedElement={<UserDetailsNotAllowed />}
          />
        }
      />
      <Route
        path={routes.instructorDetails.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncTeacherDetails}
            permissions="student"
            notAllowedElement={<UserDetailsNotAllowed />}
          />
        }
      />
      <Route
        path={routes.sessionConversation.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncConversation}
            permissions={["student", "instructor"]}
          />
        }
      />
      <Route
        path={routes.postFeedback.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncPostSessionFeedback}
            permissions={["student", "instructor"]}
          />
        }
      />
      <Route
        path={routes.public.signInOAuth.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncOauthLoading}
            permissions={["instructor", "student"]}
          />
        }
      />
      <Route
        path={routes.course.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncCoursePage}
            permissions={["instructor", "student"]}
          />
        }
      />
      <Route
        path={routes.courseObject.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncCoursePage}
            permissions={["instructor", "student"]}
          />
        }
      />
      <Route
        path={routes.libraryRoot.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncLibraryRoot}
            permissions={["instructor", "student"]}
          />
        }
      />
      <Route
        path={routes.libraryObject.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncLibrary}
            permissions={["instructor", "student"]}
          />
        }
      />
      <Route
        path={routes.teamDashboardParams.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncTeamDashboard}
            permissions={["team-manager", "company-manager"]}
          />
        }
      />
      <Route
        path={routes.adaptiveTest.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncAdaptiveTestPage}
            permissions={usr => usr.student?.access.adaptive_test === true}
          />
        }
      />
      <Route
        path={routes.practice.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncPracticePage}
            permissions={usr => usr.student?.access.practice_exercises === true}
          />
        }
      />
      <Route
        path={routes.communicationSkills.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncSkillsPage}
            permissions={usr => usr.student?.access.practice_exercises === true}
          />
        }
      />
      <Route
        path={routes.talkToTally.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncTalkToTallyPage}
            permissions={usr => usr.canAccessTally}
          />
        }
      />
      <Route
        path={routes.teamDashboard.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncTeamsPage}
            permissions={["team-manager", "company-manager"]}
          />
        }
      />
      <Route
        path={routes.companyReportsPage.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncCompanyReportsPage}
            permissions="company-manager"
          />
        }
      />
      <Route
        path={routes.teamMemberDetails.pattern}
        element={
          <RestrictedRoute
            Component={Pages.AsyncTeamMember}
            permissions={["team-manager", "company-manager"]}
          />
        }
      />
      <Route
        path={routes.payment.pattern}
        element={<Navigate to={routes.root.path()} replace />}
      />
      <Route path={routes.updatePassword.pattern} element={<ChangeUserPassword />} />

      <Route
        path={routes.public.signUpWithCode.pattern}
        element={<Navigate to={routes.settingsBilling.path()} replace />}
      />

      <Route
        path={routes.public.selfSignUp.pattern}
        element={<Navigate to={routes.settingsBilling.path()} replace />}
      />

      <Route path={routes.error.pattern} element={<ErrorPage />} />

      <Route path={routes.signOut.pattern} element={<SignOut />} />

      <Route path="*" element={<Navigate to={routes.root.path()} replace />} />
    </Routes>
  );
}
